/**
 * Custom render functions with providers
 * @module @orchard9ai/testing-utils/render
 */

import React, { ReactElement, ReactNode } from 'react';
import { render as rtlRender, RenderOptions, RenderResult } from '@testing-library/react';
import { queries } from '@testing-library/dom';
import type { ProvidersConfig } from './types';

/**
 * Default test providers wrapper
 */
export const createWrapper = (config?: ProvidersConfig) => {
  return ({ children }: { children: ReactNode }) => {
    let content = children;

    // Wrap with theme provider if enabled
    if (config?.theme !== false && config?.providers?.theme) {
      const ThemeProvider = config.providers.theme;
      content = React.createElement(ThemeProvider, { theme: config.themeConfig }, content);
    }

    // Wrap with router provider if enabled
    if (config?.router !== false && config?.providers?.router) {
      const RouterProvider = config.providers.router;
      content = React.createElement(RouterProvider, config.routerConfig, content);
    }

    // Wrap with auth provider if enabled
    if (config?.auth !== false && config?.providers?.auth) {
      const AuthProvider = config.providers.auth;
      content = React.createElement(AuthProvider, config.authConfig, content);
    }

    // Wrap with i18n provider if enabled
    if (config?.i18n !== false && config?.providers?.i18n) {
      const I18nProvider = config.providers.i18n;
      content = React.createElement(I18nProvider, config.i18nConfig, content);
    }

    // Wrap with custom providers
    if (config?.customProviders) {
      for (const Provider of config.customProviders) {
        content = React.createElement(Provider, { children: content });
      }
    }

    return React.createElement(React.Fragment, {}, content);
  };
};

/**
 * Render with all configured providers
 */
export function renderWithProviders(
  ui: ReactElement,
  options?: RenderOptions & { providerConfig?: ProvidersConfig }
): RenderResult {
  const { providerConfig, ...renderOptions } = options || {};

  const wrapper = createWrapper(providerConfig);

  return rtlRender(ui, {
    wrapper,
    ...renderOptions,
  });
}

/**
 * Render with default providers
 */
export function render(ui: ReactElement, options?: RenderOptions): RenderResult {
  return renderWithProviders(ui, options);
}

/**
 * Render hook with providers
 */
export function renderHook<TProps, TResult>(
  callback: (props: TProps) => TResult,
  options?: RenderOptions & { providerConfig?: ProvidersConfig; initialProps?: TProps }
): {
  result: TResult;
  rerender: (props?: TProps) => void;
} & Omit<RenderResult, 'rerender'> {
  const { providerConfig, initialProps, ...renderOptions } = options || {};

  const wrapper = createWrapper(providerConfig);

  let result: TResult;

  const TestComponent = () => {
    result = callback(initialProps as TProps);
    return null;
  };

  const { rerender: baseRerender, ...renderResult } = rtlRender(
    React.createElement(TestComponent),
    {
      wrapper,
      ...renderOptions,
    }
  );

  return {
    result: result!,
    rerender: (props?: TProps) => {
      const NewComponent = () => {
        result = callback(props || (initialProps as TProps));
        return null;
      };
      baseRerender(React.createElement(NewComponent));
    },
    ...renderResult,
  };
}

// Re-export everything from testing-library
export * from '@testing-library/react';
export { queries };
